import { Component, Input, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Article, BackendTag } from '@meta-press/shared-plugins';
import { SourceMaterialService } from '../source-material-service/source-material.service';
import { NotificationService } from '../notification-service/notification.service';
import { TagManagementModalComponent } from '../tag-management-modal/tag-management-modal.component';
import { forkJoin, of, catchError, Observable, EMPTY, combineLatest, map } from 'rxjs';

@Component({
	selector: 'app-add-tags-modal',
	standalone: true,
	imports: [CommonModule, FormsModule],
	templateUrl: './add-tags-modal.component.html',
	styleUrls: ['./add-tags-modal.component.scss'],
})
export class AddTagsModalComponent implements OnInit {
	@Input() selectedArticles: Article[] = [];

	currentTagInput = '';
	isLoading = false; // Loading state for saving changes to articles
	isCreatingTag = false; // Loading state for creating a new tag
	selectedTagIds: number[] = []; // Currently selected tag IDs for editing
	originalTagIds: number[] = []; // Original tag IDs for change tracking

	activeModal = inject(NgbActiveModal);
	modalService = inject(NgbModal);
	sourceMaterialService = inject(SourceMaterialService);
	notificationService = inject(NotificationService);

	ngOnInit(): void {
		// If single file selected, populate selectedTagIds with existing tags for editing
		if (this.selectedArticles.length === 1) {
			// Handle both cases: file with tags and file without tags
			const existingTags = this.selectedArticles[0]?.tags || [];
			this.originalTagIds = [...existingTags];
			this.selectedTagIds = [...existingTags];
		} else {
			// For multiple files, no original tags to track
			this.originalTagIds = [];
		}
	}

	// Add existing tag from the available tags list
	addExistingTag(tagName: string): void {
		const tag = this.sourceMaterialService.GetTagByName(tagName);
		if (tag && !this.selectedTagIds.includes(tag.id)) {
			this.selectedTagIds.push(tag.id);
		}
	}

	// Toggle existing tag selection (add if not selected, remove if selected)
	toggleExistingTag(tag: BackendTag): void {
		if (this.selectedTagIds.includes(tag.id)) {
			this.removeTag(tag.id);
		} else {
			this.addExistingTag(tag.name);
		}
	}

	// Add tag to the selected tags list
	addTag(): void {
		const tagName = this.currentTagInput.trim();
		if (tagName) {
			const existingTag =
				this.sourceMaterialService.GetTagByName(tagName);
			if (existingTag && !this.selectedTagIds.includes(existingTag.id)) {
				// Tag exists, add its ID
				this.selectedTagIds.push(existingTag.id);
				this.currentTagInput = '';
			} else if (
				existingTag &&
				this.selectedTagIds.includes(existingTag.id)
			) {
				// Tag already selected, just clear input
				this.currentTagInput = '';
			} else if (!existingTag) {
				// Tag doesn't exist, create it using sourceMaterialService
				this.isCreatingTag = true;
				this.sourceMaterialService
					.AddTag({
						Name: tagName,
						Description: tagName,
					})
					.subscribe({
						next: (createdTag) => {
							// Add the newly created tag to selectedTagIds
							this.selectedTagIds.push(createdTag.id);
							this.currentTagInput = '';
							this.isCreatingTag = false;

							console.log(`New tag "${createdTag.name}" created and selected. User can now click "Tallenna muutokset" to apply tags.`);
						},
						error: (error) => {
							this.isCreatingTag = false;
							console.error('Error creating tag:', error);
							this.notificationService.ShowToast(
								'Tagin luominen epäonnistui: ' + error,
								'danger',
							);
						},
					});
			}
		}
	}

	// Remove tag from the selected tags list
	removeTag(tagId: number): void {
		const index = this.selectedTagIds.indexOf(tagId);
		if (index > -1) {
			this.selectedTagIds.splice(index, 1);
		}
	}

	// Get current tags for the single selected article (for display purposes)
	getCurrentArticleTags(): number[] {
		if (
			this.selectedArticles.length === 1 &&
			this.selectedArticles[0]?.tags
		) {
			return this.selectedArticles[0].tags;
		}
		return [];
	}

	// Check if there are any changes to save
	hasChanges(): boolean {
		// For multiple files: any selected tags constitute changes
		if (this.selectedArticles.length > 1) {
			return this.selectedTagIds.length > 0;
		}

		// For single file: check if selected tags differ from original tags
		if (this.selectedArticles.length === 1) {
			// Check if arrays are different (different length or different contents)
			if (this.selectedTagIds.length !== this.originalTagIds.length) {
				return true;
			}

			// Check if any selected tag is not in original tags
			const hasNewTags = this.selectedTagIds.some(
				(tagId) => !this.originalTagIds.includes(tagId),
			);
			const hasRemovedTags = this.originalTagIds.some(
				(tagId) => !this.selectedTagIds.includes(tagId),
			);

			return hasNewTags || hasRemovedTags;
		}

		return false;
	}

	// Update tags for selected articles (add/remove for single file, add only for multiple files)
	addTagsToSelectedArticles(): void {
		if (this.selectedArticles.length === 0) {
			this.notificationService.ShowToast(
				'Ei valittuja artikkeleita',
				'warning',
			);
			return;
		}

		// Enhanced logging for debugging
		console.group('🏷️ Tag Assignment Operation Started');
		console.log('Selected articles:', this.selectedArticles.map(a => ({ id: a.id, title: a.title, currentTags: a.tags })));
		console.log('Selected tag IDs:', this.selectedTagIds);
		console.log('Operation type:', this.selectedArticles.length === 1 ? 'Single file (add/remove)' : 'Multiple files (add only)');
		console.log('Original tag IDs (for single file):', this.originalTagIds);
		console.groupEnd();

		this.isLoading = true;

		// Tags are created immediately when added, so proceed directly with assignments
		this.performTagAssignments();
	}

	private performTagAssignments(): void {
		const startTime = performance.now();
		
		interface TagOperation {
			observable: Observable<any>;
			articleId: number;
			tagId: number;
			operation: 'add' | 'remove';
			articleTitle?: string;
		}

		const tagOperations: TagOperation[] = [];

		this.selectedArticles.forEach((article) => {
			if (this.selectedArticles.length === 1) {
				// For single file: handle both adding and removing tags
				const originalTags = article.tags || [];
				const tagsToAdd = this.selectedTagIds.filter(
					(tagId) => !originalTags.includes(tagId),
				);
				const tagsToRemove = originalTags.filter(
					(tagId) => !this.selectedTagIds.includes(tagId),
				);

				// Add new tags
				tagsToAdd.forEach((tagId) => {
					tagOperations.push({
						observable: this.sourceMaterialService.AddTagToArticle(tagId, article.id),
						articleId: article.id,
						tagId: tagId,
						operation: 'add',
						articleTitle: article.title
					});
				});

				// Remove unselected tags
				tagsToRemove.forEach((tagId) => {
					tagOperations.push({
						observable: this.sourceMaterialService.RemoveTagFromArticle(tagId, article.id),
						articleId: article.id,
						tagId: tagId,
						operation: 'remove',
						articleTitle: article.title
					});
				});
			} else {
				// For multiple files: only add tags (don't remove existing ones)
				this.selectedTagIds.forEach((tagId) => {
					// Only add if the article doesn't already have this tag
					if (!article.tags || !article.tags.includes(tagId)) {
						tagOperations.push({
							observable: this.sourceMaterialService.AddTagToArticle(tagId, article.id),
							articleId: article.id,
							tagId: tagId,
							operation: 'add',
							articleTitle: article.title
						});
					}
				});
			}
		});

		if (tagOperations.length === 0) {
			this.isLoading = false;
			this.notificationService.ShowToast(
				'Ei muutoksia tehtävänä',
				'primary',
			);
			return;
		}

		const operationStartTime = performance.now();
		console.log(`🚀 Starting ${tagOperations.length} tag operations...`);
		console.table(tagOperations.map((op, i) => ({
			'#': i + 1,
			'Operation': op.operation,
			'Article': `${op.articleTitle} (ID: ${op.articleId})`,
			'Tag ID': op.tagId
		})));

		// Execute all operations with individual error handling
		const wrappedOperations = tagOperations.map((op, index) =>
			op.observable.pipe(
				map(() => ({ success: true, operation: op, index })),
				catchError((error) => {
					console.error(`❌ Tag operation failed: ${op.operation} tag ${op.tagId} to article ${op.articleId} (${op.articleTitle})`, error);
					return of({ success: false, operation: op, error, index });
				})
			)
		);

		forkJoin(wrappedOperations).subscribe({
			next: (results) => {
				this.isLoading = false;
				const operationEndTime = performance.now();
				
				const successful = results.filter(r => r.success);
				const failed = results.filter(r => !r.success);
				
				console.group('🏁 Tag Operations Completed');
				console.log(`⏱️ Total operation time: ${(operationEndTime - startTime).toFixed(2)}ms`);
				console.log(`⏱️ Server requests time: ${(operationEndTime - operationStartTime).toFixed(2)}ms`);
				console.log(`✅ Successful: ${successful.length}, ❌ Failed: ${failed.length}`);
				
				if (failed.length > 0) {
					console.group('❌ Failed Operations');
					failed.forEach(f => {
						const failedResult = f as { success: boolean; operation: any; error: any; index: number; };
						console.error(`${failedResult.operation.operation} tag ${failedResult.operation.tagId} to article ${failedResult.operation.articleId}:`, failedResult.error);
					});
					console.groupEnd();
				}
				console.groupEnd();

				if (failed.length === 0) {
					// All operations succeeded - local updates already done by SourceMaterialService
					const action = this.selectedArticles.length === 1 ? 'päivitetty' : 'lisätty';
					this.notificationService.ShowToast(
						`Tagit ${action} onnistuneesti ${this.selectedArticles.length} artikkeliin`,
						'success',
					);
					this.activeModal.close();
				} else if (successful.length === 0) {
					// All operations failed
					this.notificationService.ShowToast(
						'Tagien päivittäminen epäonnistui kokonaan',
						'danger',
					);
				} else {
					// Partial success
					const failedArticles = [...new Set(failed.map(f => f.operation.articleTitle || f.operation.articleId))];
					this.notificationService.ShowToast(
						`Tagien päivittäminen onnistui osittain. ${successful.length}/${results.length} operaatiota onnistui. Epäonnistuneet artikkelit: ${failedArticles.slice(0, 3).join(', ')}${failedArticles.length > 3 ? '...' : ''}`,
						'warning',
					);

					// Don't close modal on partial failure so user can retry
				}
			},
			error: (error) => {
				this.isLoading = false;
				console.error('Unexpected error in tag operations:', error);
				this.notificationService.ShowToast(
					'Odottamaton virhe tagien päivittämisessä: ' + error,
					'danger',
				);
			}
		});
	}

	/**
	 * Get tags sorted alphabetically by name
	 * @returns Array of tags sorted alphabetically
	 */
	get sortedTags(): BackendTag[] {
		return [...this.sourceMaterialService.Tags].sort((a, b) =>
			a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
		);
	}

	close(): void {
		if (!this.isLoading) {
			this.activeModal.dismiss();
		}
	}



	/**
	 * Open tag management modal on top of current modal
	 */
	openTagManagement(): void {
		this.modalService.open(TagManagementModalComponent, {
			size: 'lg',
			backdrop: 'static',
			keyboard: false,
		});
	}
}
